import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { PolicyService } from '@services/policy.service';
import { LoadingComponent } from '@components/shared';
import { PolicyFamily, PolicyListItemResponse, POLICY_FAMILIES } from '@models/policy.model';

const PAGE_SIZE = 20;

type StatusFilter = '' | 'draftOnly' | 'active' | 'inactive';

@Component({
  selector: 'app-policy-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, RouterLinkActive, LoadingComponent],
  templateUrl: './policy-list.component.html',
  styleUrl: './policy-list.component.scss'
})
export class PolicyListComponent implements OnInit, OnDestroy {
  readonly families = POLICY_FAMILIES;

  family: PolicyFamily = 'payment';
  items: PolicyListItemResponse[] = [];
  loading = false;
  errorMessage: string | null = null;

  search = '';
  status: StatusFilter = '';
  hasDraft = false;

  page = 1;
  hasNextPage = false;

  private searchChanged = new Subject<string>();
  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private policyService: PolicyService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.searchChanged.pipe(debounceTime(350), distinctUntilChanged()).subscribe(() => {
        this.page = 1;
        this.updateQueryParamsAndReload();
      })
    );

    this.subscriptions.add(
      this.route.paramMap.subscribe(params => {
        this.family = (params.get('family') as PolicyFamily) ?? 'payment';
        this.readFiltersFromQueryParams();
        this.load();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onSearchInput(value: string): void {
    this.search = value;
    this.searchChanged.next(value);
  }

  onFilterChange(): void {
    this.page = 1;
    this.updateQueryParamsAndReload();
  }

  onPageChange(page: number): void {
    if (page < 1) return;
    this.page = page;
    this.updateQueryParamsAndReload();
  }

  goToDetail(item: PolicyListItemResponse): void {
    this.router.navigate(['/policies', this.family, item.id]);
  }

  retry(): void {
    this.load();
  }

  statusBadgeClass(status: string): string {
    switch (status) {
      case 'active':
        return 'text-bg-success';
      case 'inactive':
        return 'text-bg-secondary';
      case 'draftOnly':
        return 'text-bg-warning';
      default:
        return 'text-bg-light';
    }
  }

  private readFiltersFromQueryParams(): void {
    const qp = this.route.snapshot.queryParamMap;
    this.search = qp.get('search') ?? '';
    this.status = (qp.get('status') as StatusFilter) ?? '';
    this.hasDraft = qp.get('hasDraft') === 'true';
    this.page = Number(qp.get('page') ?? 1);
  }

  private updateQueryParamsAndReload(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        search: this.search || null,
        status: this.status || null,
        hasDraft: this.hasDraft ? 'true' : null,
        page: this.page > 1 ? this.page : null
      },
      queryParamsHandling: 'merge'
    });
    this.load();
  }

  private load(): void {
    this.loading = true;
    this.errorMessage = null;

    this.policyService
      .listPolicies(this.family, {
        search: this.search || undefined,
        status: this.status || undefined,
        hasDraft: this.hasDraft || undefined,
        top: PAGE_SIZE,
        skip: (this.page - 1) * PAGE_SIZE,
        maxpagesize: PAGE_SIZE
      })
      .subscribe({
        next: page => {
          this.items = page.value;
          this.hasNextPage = !!page.nextLink;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: Error) => {
          this.errorMessage = err.message;
          this.items = [];
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
  }
}
